import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';

import { cn } from '../../utils/index';

const buttonVariants = cva(
    'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:bg-gray-400 disabled:border-gray-400 disabled:text-gray-50 dark:disabled:text-white',
    {
        variants: {
            variant: {
                default: 'bg-primary text-white  hover:bg-primary-hover',
                outline: 'border border-sub text-sub disabled:border-gray-400 hover:bg-hover',
                outlinePoint: 'border border-point text-point rounded-[10px] font-medium',
                link: 'text-point underline-offset-4 hover:underline',
                ghost: 'hover:bg-accent hover:text-accent-foreground',
            },
            size: {
                default: 'h-[41px] px-5',
                xs: 'rounded px-2 py-1 text-sm',
                sm: 'h-[34px] rounded-md px-3 text-sm font-medium',
                md: 'h-10 rounded-lg py-[10px] pr-4 pl-[14px] text-sm font-medium',
                lg: 'h-[50px] text-base font-medium',
                xl: 'h-[62px] rounded-xl text-[18px] max-xs:text-base font-medium',
                icon: 'w-7 h-7',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
    }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
